import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, Trash2, FileText, Check,
  RefreshCw, DollarSign, Calendar, Tag, ArrowRight,
  Image, AlertCircle, ChevronDown, RotateCw
} from "lucide-react";

const CATEGORIES = [
  { value: "food", label: "Food/Groceries" },
  { value: "cable", label: "Cable/Internet" },
  { value: "electric", label: "Electric" },
  { value: "water", label: "Water" },
  { value: "garbage", label: "Garbage" },
  { value: "car_payment", label: "Car Payment" },
  { value: "insurance", label: "Insurance" },
  { value: "night_out", label: "Night Out" },
  { value: "maintenance", label: "Maintenance" },
  { value: "subscription", label: "Subscriptions" },
  { value: "other", label: "Other" },
];

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

export default function ReceiptScanner() {
  const { api } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ name: "", amount: "", category: "other", date: "", description: "" });
  const [imageUrls, setImageUrls] = useState({});
  const fileInputRef = useRef(null);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/receipts");
      setReceipts(res.data);
      // Load images for each receipt
      for (const r of res.data) {
        if (r.content_type?.startsWith("image/") && !imageUrls[r.id]) {
          loadImage(r.id);
        }
      }
    } catch {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadReceipts(); }, [loadReceipts]);

  const loadImage = async (receiptId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/receipts/${receiptId}/image?auth=${token}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setImageUrls((prev) => ({ ...prev, [receiptId]: url }));
    } catch {
      // Image load failed - not critical
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload an image (JPEG, PNG, WebP) or PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/receipts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.status === "parsed" ? "Receipt uploaded and parsed!" : "Receipt uploaded");
      loadReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openCreateExpense = (receipt) => {
    const parsed = receipt.parsed_data || {};
    setSelectedReceipt(receipt);
    setExpenseForm({
      name: parsed.merchant || "",
      amount: parsed.total ? String(parsed.total) : "",
      category: parsed.category || "other",
      date: parsed.date || new Date().toISOString().split("T")[0],
      description: `Receipt: ${receipt.original_filename}`,
    });
    setShowExpenseDialog(true);
  };

  const createExpense = async () => {
    if (!expenseForm.name || !expenseForm.amount) {
      toast.error("Name and amount are required");
      return;
    }
    try {
      await api.post(`/receipts/${selectedReceipt.id}/create-expense`, {
        name: expenseForm.name,
        amount: parseFloat(expenseForm.amount),
        category: expenseForm.category,
        date: expenseForm.date,
        description: expenseForm.description,
      });
      toast.success("Expense created from receipt");
      setShowExpenseDialog(false);
      loadReceipts();
    } catch {
      toast.error("Failed to create expense");
    }
  };

  const deleteReceipt = async (id) => {
    try {
      await api.delete(`/receipts/${id}`);
      toast.success("Receipt deleted");
      loadReceipts();
    } catch {
      toast.error("Failed to delete receipt");
    }
  };

  const reparseReceipt = async (id) => {
    try {
      toast.info("Re-parsing receipt...");
      await api.post(`/receipts/${id}/reparse`);
      toast.success("Receipt re-parsed");
      loadReceipts();
    } catch {
      toast.error("Failed to re-parse");
    }
  };

  if (loading) {
    return (
      <Layout title="Receipt Scanner">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Receipt Scanner">
      <div className="space-y-6" data-testid="receipt-scanner-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Receipt Scanner</h1>
            <p className="text-sm text-zinc-500 mt-1">Upload receipts, auto-extract data with AI</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleUpload}
              className="hidden"
              data-testid="file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Upload Receipt
            </Button>
            <Button onClick={loadReceipts} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-receipts-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Drop Zone */}
        <Card
          className="bg-[#0A0A0A] border-white/10 border-dashed rounded-none cursor-pointer hover:border-emerald-500/30 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          data-testid="drop-zone"
        >
          <CardContent className="py-10 text-center">
            <Camera className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">Click to upload a receipt image</p>
            <p className="text-xs text-zinc-600 mt-1">JPEG, PNG, WebP, or PDF (max 10MB)</p>
            {uploading && (
              <div className="mt-3 flex items-center justify-center gap-2 text-cyan-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Processing receipt...</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {receipts.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-receipts">
              <CardContent className="p-4">
                <p className="text-xs text-zinc-500 uppercase">Total Receipts</p>
                <p className="text-xl font-bold font-mono text-white mt-1">{receipts.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-parsed">
              <CardContent className="p-4">
                <p className="text-xs text-zinc-500 uppercase">Parsed</p>
                <p className="text-xl font-bold font-mono text-emerald-400 mt-1">{receipts.filter((r) => r.status === "parsed" || r.status === "linked").length}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-linked">
              <CardContent className="p-4">
                <p className="text-xs text-zinc-500 uppercase">Linked to Expenses</p>
                <p className="text-xl font-bold font-mono text-cyan-400 mt-1">{receipts.filter((r) => r.expense_id).length}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Receipt List */}
        {receipts.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-12 text-center">
              <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No receipts uploaded yet</p>
              <p className="text-zinc-600 text-xs mt-1">Upload a receipt to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="receipt-list">
            {receipts.map((receipt) => {
              const parsed = receipt.parsed_data || {};
              const isLinked = !!receipt.expense_id;
              const isParsed = receipt.status === "parsed" || receipt.status === "linked";
              return (
                <Card key={receipt.id} className={`bg-[#0A0A0A] rounded-none ${isLinked ? "border-emerald-500/10" : "border-white/10"}`} data-testid={`receipt-${receipt.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 bg-white/5 rounded-sm flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {imageUrls[receipt.id] ? (
                          <img src={imageUrls[receipt.id]} alt="Receipt" className="w-full h-full object-cover" />
                        ) : (
                          <Image className="w-6 h-6 text-zinc-600" />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white truncate">
                            {parsed.merchant || receipt.original_filename}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                            isLinked ? "bg-emerald-500/10 text-emerald-400" :
                            isParsed ? "bg-cyan-500/10 text-cyan-400" :
                            receipt.status === "parse_failed" ? "bg-red-500/10 text-red-400" :
                            "bg-zinc-500/10 text-zinc-400"
                          }`}>
                            {isLinked ? "Linked" : isParsed ? "Parsed" : receipt.status === "parse_failed" ? "Failed" : "Uploaded"}
                          </span>
                        </div>

                        {isParsed && (
                          <div className="flex items-center gap-4 text-xs text-zinc-500 mb-1">
                            {parsed.total && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" /> {fmt(parsed.total)}
                              </span>
                            )}
                            {parsed.date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {parsed.date}
                              </span>
                            )}
                            {parsed.category && (
                              <span className="flex items-center gap-1 capitalize">
                                <Tag className="w-3 h-3" /> {parsed.category}
                              </span>
                            )}
                            {parsed.confidence && (
                              <span className="text-zinc-600">
                                {Math.round(parsed.confidence * 100)}% confidence
                              </span>
                            )}
                          </div>
                        )}

                        {parsed.items?.length > 0 && (
                          <div className="text-[10px] text-zinc-600 truncate">
                            {parsed.items.slice(0, 3).map((i) => i.name).join(", ")}
                            {parsed.items.length > 3 && ` +${parsed.items.length - 3} more`}
                          </div>
                        )}

                        <p className="text-[10px] text-zinc-700 mt-1">
                          {receipt.original_filename} - {new Date(receipt.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {isParsed && !isLinked && (
                          <Button
                            onClick={() => openCreateExpense(receipt)}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs h-7"
                            data-testid={`create-expense-${receipt.id}`}
                          >
                            <ArrowRight className="w-3 h-3 mr-1" /> Add Expense
                          </Button>
                        )}
                        {isLinked && (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-1 px-2">
                            <Check className="w-3 h-3" /> Expense Created
                          </span>
                        )}
                        {receipt.status === "parse_failed" && (
                          <Button
                            onClick={() => reparseReceipt(receipt.id)}
                            variant="outline" size="sm"
                            className="border-amber-500/20 text-amber-400 rounded-sm text-xs h-7"
                            data-testid={`reparse-${receipt.id}`}
                          >
                            <RotateCw className="w-3 h-3 mr-1" /> Retry
                          </Button>
                        )}
                        <Button
                          onClick={() => deleteReceipt(receipt.id)}
                          variant="ghost" size="sm"
                          className="text-zinc-600 hover:text-red-400 h-7"
                          data-testid={`delete-receipt-${receipt.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Expense Dialog */}
        <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md" data-testid="expense-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Create Expense from Receipt</DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">Review and adjust the extracted data</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs">Merchant / Name</Label>
                <Input
                  value={expenseForm.name}
                  onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })}
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="expense-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Amount ($)</Label>
                  <Input
                    type="number" step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    className="bg-black/50 border-white/10 rounded-sm"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Date</Label>
                  <Input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    className="bg-black/50 border-white/10 rounded-sm"
                    data-testid="expense-date-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Category</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={createExpense} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="confirm-expense-btn">
                  Create Expense
                </Button>
                <Button onClick={() => setShowExpenseDialog(false)} variant="outline" className="border-white/10 text-zinc-400 rounded-sm">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
